import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, CircleIcon, LockIcon, PlayIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Seo } from '../../components/Seo';
import { JoinDialog } from '../../components/academy/JoinDialog';
import { QuizBlock } from '../../components/academy/QuizBlock';
import { ProgressBar } from '../../components/academy/ProgressBar';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { courseProgress, isLessonUnlocked, lessonKey, lessonPath, moduleProgress, parseLessonParam } from '../../utils/academyProgress';
import type { Learner } from '../../types/academy';
import { NotFound } from '../NotFound';
export function AcademyLesson() {
  const {
    slug,
    lesson
  } = useParams<{
    slug: string;
    lesson: string;
  }>();
  const {
    findCourse
  } = useAcademyHub();
  const course = findCourse(slug ?? '');
  const position = parseLessonParam(lesson);
  const {
    state,
    isEnrolled,
    enrol,
    completeLesson,
    touch
  } = useAcademy();
  const [joinOpen, setJoinOpen] = React.useState(false);
  const enrolled = course ? isEnrolled(course.slug) : false;
  React.useEffect(() => {
    if (course && enrolled) touch(course.slug);
  }, [course, enrolled, touch, lesson]);
  if (!course || !position) return <NotFound data-id="element-1784" data-mp="1rgxzl1gn7bul" />;
  const {
    moduleIndex,
    lessonIndex
  } = position;
  const mod = course.modules[moduleIndex];
  if (!mod || !mod.lessons[lessonIndex]) return <Navigate to={`/academy/courses/${course.slug}`} replace data-id="element-1785" data-mp="1ihkok4cvef7r" />;
  const unlocked = isLessonUnlocked(moduleIndex, enrolled);
  const title = mod.lessons[lessonIndex];
  const done = Boolean(state.completions[lessonKey(course.slug, moduleIndex, lessonIndex)]);
  const mp = moduleProgress(course, moduleIndex, state);
  const cp = courseProgress(course, state);
  const isLastInModule = lessonIndex === mod.lessons.length - 1;
  const nextPos = isLastInModule ? moduleIndex + 1 < course.modules.length ? {
    moduleIndex: moduleIndex + 1,
    lessonIndex: 0
  } : null : {
    moduleIndex,
    lessonIndex: lessonIndex + 1
  };
  const prevPos = lessonIndex > 0 ? {
    moduleIndex,
    lessonIndex: lessonIndex - 1
  } : moduleIndex > 0 ? {
    moduleIndex: moduleIndex - 1,
    lessonIndex: course.modules[moduleIndex - 1].lessons.length - 1
  } : null;
  const nextLocked = nextPos ? !isLessonUnlocked(nextPos.moduleIndex, enrolled) : false;
  const certificate = state.certificates[course.slug];
  const onJoined = (learner: Learner) => {
    void enrol(course, learner);
    setJoinOpen(false);
  };
  const askToEnrol = () => {
    if (state.learner) void enrol(course);else setJoinOpen(true);
  };
  return <>
      <Seo title={`${title}, ${course.title}`} description={course.description} path={`/academy/learn/${course.slug}/${lesson}`} noIndex data-id="element-1786" data-mp="2y1to9ax123xg" />

      <div className="border-b border-line" data-id="element-1787" data-mp="2jeml3rdsaa94">
        <Container className="flex flex-wrap items-center gap-x-6 gap-y-3 py-4" data-id="element-1788" data-mp="2jfo0g81u1iwn">
          <Link to={`/academy/courses/${course.slug}`} className="flex items-center gap-2 text-[14px] text-body hover:text-ink" data-id="element-1789" data-mp="2egbb9gnhnzqw">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1790" data-mp="2tedhxzpth1cd" />
            <span className="max-w-[46ch] truncate" data-id="element-1791" data-mp="2odg05o8py9be">{course.title}</span>
          </Link>
          {enrolled && <div className="ml-auto flex w-48 items-center gap-3" data-id="element-1792" data-mp="32cffu6e97dvf">
              <ProgressBar percent={cp.percent} complete={cp.complete} label="Course progress" data-id="element-1793" data-mp="2xenj8z2nz3kw" />
              <span className="shrink-0 text-small text-ink" data-id="element-1794" data-mp="3c30ji88sb8l7">{cp.percent}%</span>
            </div>}
        </Container>
      </div>

      <Container className="grid gap-10 py-8 sm:py-10 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1795" data-mp="375i88b7aoha1">
        <div className="min-w-0" data-id="element-1796" data-mp="3l4zzcx82c6mk">
          {unlocked ? <>
              <div className="relative flex aspect-video w-full flex-col items-center justify-center overflow-hidden rounded-md border border-line bg-[#04152B]" data-id="element-1797" data-mp="3h2wmc45qwtz6">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold" aria-hidden="true" data-id="element-1798" data-mp="1edve1yiwq47b">
                  <PlayIcon className="ml-1 h-7 w-7" data-id="element-1799" data-mp="1o4ziw5evc8of" />
                </span>
                <p className="mt-4 text-[15px] text-ink" data-id="element-1800" data-mp="1t4m5h5gl719w">Video lesson in production</p>
                <p className="mt-1 max-w-sm px-6 text-center text-small text-muted" data-id="element-1801" data-mp="1t6f35v982b5r">
                  Recorded lessons are being added course by course. You can still work through the module and its quiz.
                </p>
              </div>

              <p className="mt-8 text-small text-muted" data-id="element-1802" data-mp="15errulog18ol">
                Module {moduleIndex + 1}: {mod.title} · Lesson {lessonIndex + 1} of {mod.lessons.length}
              </p>
              <h1 className="mt-2 font-display text-[26px] leading-tight text-ink sm:text-[32px]" data-id="element-1803" data-mp="2gugetd1p67pe">{title}</h1>
              {mod.lessonNotes?.[lessonIndex]?.trim() && <div className="mt-5 max-w-2xl space-y-3 text-[16px] leading-relaxed text-body" data-id="element-1804" data-mp="2kxmlumwmw2wx">
                  {mod.lessonNotes[lessonIndex].trim().split(/\n{2,}/).map((para, i) => <p key={i} className="whitespace-pre-line" data-id="element-1805" data-mp="f33idgqjyrm" data-mpi={i}>
                        {para}
                      </p>)}
                </div>}

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1806" data-mp="3mnum0xi0m0l7">
                {done ? <span className="inline-flex items-center gap-2 rounded-sm border border-[#4ADE80]/40 px-4 py-3 text-[15px] text-[#4ADE80]" data-id="element-1807" data-mp="3hnqa7ks1xu1b">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1808" data-mp="3dmwnguqcila0" />
                    Completed
                  </span> : <Button type="button" onClick={() => completeLesson(course, moduleIndex, lessonIndex)} data-id="element-1809" data-mp="1bvtdm7oa5aqi">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1810" data-mp="16vx823zxpb8u" />
                    Mark as complete
                  </Button>}
                {nextPos && !(isLastInModule && mp.hasQuiz && !mp.quizPassed) && (nextLocked ? <Button type="button" variant="secondary" onClick={askToEnrol} data-id="element-1811" data-mp="1h03x57kjoh5w">
                      Enrol to continue (free)
                    </Button> : <Button to={lessonPath(course.slug, nextPos.moduleIndex, nextPos.lessonIndex)} variant="secondary" data-id="element-1812" data-mp="1lx3yj56g8vqo">
                      Next lesson
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1813" data-mp="2dpcxqgtnc26y" />
                    </Button>)}
              </div>

              {isLastInModule && <div className="mt-12" data-id="element-1814" data-mp="24on51lzomrxg">
                  {mp.hasQuiz ? mp.lessonsDone ? <QuizBlock course={course} moduleIndex={moduleIndex} data-id="element-1815" data-mp="o9vbh5qsvemk" /> : <div className="rounded-md border border-dashed border-line px-5 py-5 text-[15px] text-body" data-id="element-1816" data-mp="tc439yrxsan3">
                        Complete all {mp.total} lessons in this module to open the quiz: {mod.quizFocus.toLowerCase()}.
                      </div> : <div className="rounded-md border border-dashed border-line px-5 py-5 text-[15px] text-body" data-id="element-1817" data-mp="uqx2p79eo8vv">
                      The quiz for this module ({mod.quizFocus.toLowerCase()}) is being written. Finishing the lessons completes the module for now.
                    </div>}
                  {mp.complete && nextPos && !nextLocked && <Button to={lessonPath(course.slug, nextPos.moduleIndex, nextPos.lessonIndex)} className="mt-6" data-id="element-1818" data-mp="btm65uf5rtk4">
                      Start Module {nextPos.moduleIndex + 1}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1819" data-mp="3fh5oy724v0oc" />
                    </Button>}
                  {mp.complete && nextPos && nextLocked && <div className="mt-6 rounded-md border border-line bg-panel p-5" data-id="element-1820" data-mp="2pxix3n139sz6">
                      <p className="font-medium text-ink" data-id="element-1821" data-mp="2tym9fzjxh6vn">You have finished the free preview.</p>
                      <p className="mt-1 text-[14px] text-body" data-id="element-1822" data-mp="2yxp7h01hqcu4">Enrol for free to unlock the remaining modules and earn a certificate.</p>
                      <Button type="button" onClick={askToEnrol} className="mt-4" data-id="element-1823" data-mp="33xtn6nukr3ja">
                        Enrol for free
                      </Button>
                    </div>}
                  {!nextPos && cp.complete && certificate && <div className="mt-6 rounded-md border border-line bg-panel p-6" data-id="element-1824" data-mp="3ho6zt3iev6ey">
                      <p className="font-display text-[22px] text-ink" data-id="element-1825" data-mp="3hqje96oymyn9">Course complete</p>
                      <p className="mt-1 text-[14px] text-body" data-id="element-1826" data-mp="3mpe24n1sbde8">Your certificate is ready.</p>
                      <Button to={`/academy/certificates/${certificate.id}`} className="mt-4" data-id="element-1827" data-mp="3rk9y1ya56kwm">
                        View certificate
                      </Button>
                    </div>}
                </div>}

              {prevPos && <Link to={lessonPath(course.slug, prevPos.moduleIndex, prevPos.lessonIndex)} className="mt-12 inline-flex items-center gap-2 text-[14px] text-muted hover:text-ink" data-id="element-1828" data-mp="1fxg6kdego3c3">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1829" data-mp="1637sguq6gcxg" />
                  Previous lesson
                </Link>}
            </> : <div className="rounded-md border border-line bg-panel px-6 py-14 text-center sm:px-12" data-id="element-1830" data-mp="1yqampantm1hw">
              <LockIcon className="mx-auto h-6 w-6 text-gold" aria-hidden="true" data-id="element-1831" data-mp="1twzg9amx7mqi" />
              <h1 className="mt-4 font-display text-[26px] text-ink" data-id="element-1832" data-mp="1oz69afdsmop6">{title}</h1>
              <p className="mx-auto mt-3 max-w-md text-[15px] text-body" data-id="element-1833" data-mp="2mg4yrq0bfm13">
                This lesson is in Module {moduleIndex + 1}. Enrol for free to unlock every module, the quizzes and your certificate.
              </p>
              <Button type="button" size="lg" className="mt-7" onClick={askToEnrol} data-id="element-1834" data-mp="28v8z8c215r9">
                Enrol for free
              </Button>
            </div>}
        </div>

        <aside aria-label="Course contents" className="lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:self-start lg:overflow-y-auto" data-id="element-1835" data-mp="c7c5slxocel8">
          <h2 className="text-small font-medium text-muted" data-id="element-1836" data-mp="79ssppcpq7uv">Course contents</h2>
          <ol className="mt-3 space-y-5" data-id="element-1837" data-mp="l5ee7mjqm9ij">
            {course.modules.map((m, mi) => {
            const p = moduleProgress(course, mi, state);
            const open = isLessonUnlocked(mi, enrolled);
            return <li key={m.title} data-id="element-1838" data-mp="pxxqmg6ouylr" data-mpi={mi}>
                  <p className="flex items-start gap-2 text-[14px] font-medium text-ink" data-id="element-1839" data-mp="pyz2unzfxr52">
                    <span className="text-muted" data-id="element-1840" data-mp="14uf2d8isif8u">{mi + 1}.</span>
                    <span className="flex-1" data-id="element-1841" data-mp="14su9rog1d6dm">{m.title}</span>
                    {p.complete && <CheckCircle2Icon className="h-4 w-4 shrink-0 text-[#4ADE80]" aria-label="Module complete" data-id="element-1842" data-mp="zxyca8s15qt4" />}
                    {!open && <LockIcon className="h-3.5 w-3.5 shrink-0 text-muted" aria-label="Locked" data-id="element-1843" data-mp="1njihzmpbte2t" />}
                  </p>
                  <ul className="mt-2 space-y-0.5 border-l border-line" data-id="element-1844" data-mp="19jzj7kienf36">
                    {m.lessons.map((l, li) => {
                  const current = mi === moduleIndex && li === lessonIndex;
                  const completed = Boolean(state.completions[lessonKey(course.slug, mi, li)]);
                  const content = <>
                          {completed ? <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#4ADE80]" aria-label="Completed" data-id="element-1845" data-mp="qu15oovu8ts5" /> : <CircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1846" data-mp="vpyiwmbt1k0p" />}
                          <span data-id="element-1847" data-mp="luog3wdl18lv">{l}</span>
                        </>;
                  const cls = `-ml-px flex items-start gap-2.5 border-l py-1.5 pl-3 pr-2 text-[13px] leading-snug ${current ? 'border-gold text-ink' : 'border-transparent text-muted'}`;
                  return <li key={l} data-id="element-1848" data-mp="3p30dm85hfo10" data-mpi={li}>
                          {open ? <Link to={lessonPath(course.slug, mi, li)} aria-current={current ? 'page' : undefined} className={`${cls} transition-colors duration-150 hover:text-ink`} data-id="element-1849" data-mp="3k7l05hbdzu54">
                              {content}
                            </Link> : <span className={cls} data-id="element-1850" data-mp="6qvi2vk6f8lt">{content}</span>}
                        </li>;
                })}
                  </ul>
                </li>;
          })}
          </ol>
        </aside>
      </Container>

      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={onJoined} courseTitle={course.title} data-id="element-1851" data-mp="170mpvcigokhy" />
    </>;
}